import Backbone from "backbone";
import _ from "underscore";
import React from "react";
import { createRoot, type Root } from "react-dom/client";
import { ThemeDrive, LanguageProvider, UserFormatProvider, DateInput } from "@geotab/zenith";
import type { TSupportedLanguage } from "@geotab/zenith/dist/utils/localization/getSupportedLanguage";
import { Dialog } from "./dialog";
import { dstCalculator, getNextDayStart, getStartOfDayUserTimezone } from "../../utils/date";
import Translator from "../../utils/translator";
import { getServerDate } from "../../utils/storage/date";
import template from "./dateTimeSelect.tmpl";

interface IDateTimeSelectOptions extends Backbone.ViewOptions<Backbone.Model> {
    translator: Translator;
    title?: string;
    date?: Date;
    validate?: (dateTime: Date) => boolean;
    selectCallback: (dateTime: Date) => void;
    isUserNightMode: boolean;
    isEwd?: boolean;
    language?: TSupportedLanguage;
}

export default class DateTimeSelect extends Backbone.NativeView<Backbone.Model> {
    private readonly selectCallback;
    private readonly validate;
    private readonly translator: Translator;
    private readonly title: string | undefined;
    private readonly currentDate: Date;
    private readonly isUserNightMode: boolean;
    private readonly isEwd: boolean;
    private readonly language: TSupportedLanguage;
    private selectedDateIso: string;
    private calendarReactRoot: Root | null = null;
    private dialog: Dialog | null;

    constructor (options: IDateTimeSelectOptions) {
        super(options);
        this.selectCallback = options.selectCallback;
        this.validate = options.validate || null;
        this.title = options.title;
        this.translator = options.translator;
        let initialDate = options.date || getServerDate();
        this.currentDate = dstCalculator.toLocalDateTime(initialDate) || this.getToday();
        this.isUserNightMode = options.isUserNightMode;
        this.isEwd = options.isEwd || false;
        this.language = options.language || "en" as TSupportedLanguage;
        // Device-space ISO of the selected day (start of day) — the value the Zenith DateInput reads.
        const startOfDay = this.setStartOfDay(new Date(this.currentDate.getTime()));
        this.selectedDateIso = dstCalculator.toReverseOffsetDateTime(startOfDay).toISOString();
    }

    renderImpl () {
        let templateStr = (_.template(template))({
            selectedHour: this.currentDate.getHours(),
            selectedMinute: this.currentDate.getMinutes(),
            isEwd: this.isEwd
        });

        this.dialog = new Dialog(
            "select-datetime",
            `<div class="selectDialogContent">${ templateStr }</div>`,
            this.translator,
            this.title || "Choose start time",
            [
                [{
                    id: "datetime__done",
                    text: "Done",
                    callback: () => this.done()
                }]
            ],
            {
                isNightMode: this.isUserNightMode
            }
        );

        this.dialog.onRemove(() => this.unmountCalendar());

        return this.dialog.open()
        .then(() => {
            this.dialog?.el && this.setElement(this.dialog.el.querySelector(".selectDialogContent"));
            this.renderCalendar();
        });
    }

    render(){
        this.renderImpl();
        return this;
    }

    getSelectedDateTime () {
        let dialog = <Dialog> this.dialog;
        let hours = (<HTMLInputElement>dialog.el?.querySelector(".datetime__hours")).value;
        let minutes = (<HTMLInputElement>dialog.el?.querySelector(".datetime__minutes")).value;
        // Selected day is stored in device space; convert to the local frame the hour/minute
        // selects (and selectCallback's reverse-offset) operate in.
        let dateObj: Date = dstCalculator.toLocalDateTime(new Date(this.selectedDateIso)) || this.getToday();

        dateObj.setHours(parseInt(hours));
        dateObj.setMinutes(parseInt(minutes));
        return dateObj;
    }

    done () {
        let dateObj = this.getSelectedDateTime();
        let isValid = true;

        if (this.validate) {
            isValid = this.validate(dateObj);
        }

        if (isValid) {
            this.selectCallback && this.selectCallback(dateObj);
            this.dialog && this.dialog.remove();
        }
    }

    private renderCalendar () {
        const container = this.dialog?.el?.querySelector("#datetimeSelect_Calendar") as HTMLElement | null;
        if (!container) {
            return;
        }

        if (!this.calendarReactRoot) {
            this.calendarReactRoot = createRoot(container);
        }

        // Zenith treats disableDatesAfter as exclusive, so offset by +1 day to keep today selectable.
        // No lower bound — the driver may pick any historical date.
        const disableDatesAfter = getNextDayStart(getStartOfDayUserTimezone(getServerDate()), true).toDateString();

        this.calendarReactRoot.render(
            React.createElement(
                LanguageProvider,
                { language: this.language },
                React.createElement(
                    ThemeDrive,
                    { dark: this.isUserNightMode },
                    React.createElement(
                        UserFormatProvider,
                        {
                            toLocalDateTime: dstCalculator.toLocalDateTime,
                            toReverseOffsetDateTime: (date: Date | string) =>
                                dstCalculator.toReverseOffsetDateTime(typeof date === "string" ? new Date(date) : date)
                        },
                        React.createElement(DateInput, {
                            className: "datetime__dateInput",
                            value: this.selectedDateIso,
                            disableDatesAfter,
                            onChange: (value?: string) => {
                                if (!value) {
                                    return;
                                }
                                this.selectedDateIso = value;
                                this.renderCalendar();
                            }
                        })
                    )
                )
            )
        );
    }

    private unmountCalendar () {
        if (this.calendarReactRoot) {
            this.calendarReactRoot.unmount();
            this.calendarReactRoot = null;
        }
    }

    private setStartOfDay (date: Date) {
        date.setHours(0);
        date.setMinutes(0);
        date.setSeconds(0);
        date.setMilliseconds(0);
        return date;
    }

    private getToday () {
        let date = getServerDate();
        date = dstCalculator.toLocalDateTime(date);
        return this.setStartOfDay(date);
    }
}